import { useState, useMemo } from "react";

type SelectOption = { label: string; value: string };

type CategoryItem<T extends string> = {
    [K in T]: string;
} & { percentage: string };

type ValidationErrors = Record<string, string>;

type UseCategoryListReturn<T extends string> = {
    items: CategoryItem<T>[];
    setItems: React.Dispatch<React.SetStateAction<CategoryItem<T>[]>>;
    add: () => void;
    remove: (index: number) => void;
    update: (index: number, field: T | "percentage", value: string) => void;
    reset: () => void;
    getAvailableOptions: (currentValue: string) => SelectOption[];
    getValidationErrors: (
        selectPrefix: string,
        percentagePrefix: string,
        sumKey: string
    ) => ValidationErrors;
    canAddMore: boolean;
    sum: number;
    sumError: string | null;
};

export const useCategoryList = <T extends string>(
    valueField: T,
    options: SelectOption[]
): UseCategoryListReturn<T> => {
    const emptyItem = { [valueField]: "", percentage: "" } as CategoryItem<T>;
    const [items, setItems] = useState<CategoryItem<T>[]>([emptyItem]);

    const selectedValues = useMemo(
        () => items.map((item) => item[valueField] as string).filter(Boolean),
        [items, valueField]
    );

    const getAvailableOptions = (currentValue: string) =>
        options.filter(
            (opt) =>
                opt.value === currentValue ||
                !selectedValues.includes(opt.value)
        );

    const canAddMore = items.length < options.length;

    const { sum, sumError } = useMemo(() => {
        const percentages = items
            .map((item) => parseFloat(item.percentage) || 0)
            .filter((p) => p > 0);
        const total = percentages.reduce((acc, p) => acc + p, 0);
        return {
            sum: total,
            sumError:
                percentages.length > 0 && total !== 100
                    ? "Percentages must sum to 100"
                    : null,
        };
    }, [items]);

    const add = () => {
        if (!canAddMore) return;
        setItems((prev) => [...prev, emptyItem]);
    };

    const remove = (index: number) => {
        setItems((prev) => {
            const newItems = prev.filter((_, i) => i !== index);
            return newItems.length > 0 ? newItems : [emptyItem];
        });
    };

    const update = (index: number, field: T | "percentage", value: string) => {
        setItems((prev) => {
            const newItems = [...prev];
            newItems[index] = { ...newItems[index], [field]: value };
            return newItems;
        });
    };

    const reset = () => setItems([emptyItem]);

    const getValidationErrors = (
        selectPrefix: string,
        percentagePrefix: string,
        sumKey: string
    ): ValidationErrors => {
        const errors: ValidationErrors = {};
        items.forEach((item, index) => {
            const hasValue = !!(item[valueField] as string);
            const hasPercentage = !!item.percentage;
            if (hasValue && !hasPercentage) {
                errors[`${percentagePrefix}${index}`] =
                    "Percentage is required";
            }
            if (hasPercentage && !hasValue) {
                errors[`${selectPrefix}${index}`] = "Selection is required";
            }
        });
        if (sumError) {
            errors[sumKey] = sumError;
        }
        return errors;
    };

    return {
        items,
        setItems,
        add,
        remove,
        update,
        reset,
        getAvailableOptions,
        getValidationErrors,
        canAddMore,
        sum,
        sumError,
    };
};
